/**
 * Dock scope resolver (TASK-713) — the frontend mirror of what
 * `route_classifier.py` does server-side: pathname → entity-qualified scope
 * for the global dock (chip label, store key, context envelope).
 *
 * `entityForPath` is the TASK-694-corrected route→entity mapping, ported
 * VERBATIM from GenAIChat.tsx (which this epic deletes). It is derived from
 * the real route table in App.tsx, not raw path segments — do not re-derive.
 */

import type { ChatScope } from '../../stores/createChatStore';

export type RouteEntity = { type: 'client' | 'portfolio' | 'meeting'; id: string; name?: string };

export const entityForPath = (
  pathname: string
): { entity?: RouteEntity; portfolioId?: string } => {
  const [head, ...rest] = pathname.split('/').filter(Boolean);

  if (head === 'clients' && rest[0]) {
    return { entity: { type: 'client', id: rest[0] } };
  }
  // /portfolio/client/:clientId and /portfolio/client/:clientId/portfolio/:portfolioId.
  // Bare /portfolio and /portfolio/my-book carry no entity — leave them alone.
  if (head === 'portfolio' && rest[0] === 'client' && rest[1]) {
    return {
      entity: { type: 'client', id: rest[1] },
      portfolioId: rest[2] === 'portfolio' ? rest[3] : undefined,
    };
  }
  if (head === 'meeting' && rest[0]) {
    return { entity: { type: 'meeting', id: rest[0] } };
  }
  return {};
};

export interface DockScope extends ChatScope {
  /** Chip label prefix; entity names are enriched by the Dock at render time. */
  label: string;
  entity?: RouteEntity;
  portfolioId?: string;
}

/**
 * TASK-769 — the thread that follows a unit of work.
 *
 * > **Placement is presentation. Thread is state.** The route chooses which
 * > surface is on screen; the work chooses which conversation is in it.
 *
 * Opened by delegation, never derived from a route, so no navigation sequence
 * can conjure one. `clientId` exists solely to enforce the precedence rule.
 */
export interface ActiveTaskThread {
  taskId: string;
  /** For the chip; the thread is named, so the advisor is never guessing. */
  title: string;
  /** The task's client, or null for a client-less task. */
  clientId: string | null;
}

/**
 * TASK-769 precedence — the task thread wins ONLY where the placement names
 * nothing, or names the task's own client. Everything else keeps its own scope.
 *
 * Unqualified most-specific-wins would carry client A's task thread onto client
 * B's page. Naming the thread on the chip is not mitigation for that: the
 * failure is one client's transcript rendered under another client's name, and
 * a label does not stop it being read there.
 *
 * TASK-786/787 narrowed this twice more. The rule shipped in TASK-769 compared
 * only `entity.type === 'client'`, which is a denylist in disguise: it protects
 * the placements it recognises and yields to the task thread everywhere else.
 * Two surfaces fell through that gap, so the comparison below is now an
 * allowlist — a placement gives way only when it is provably about the same
 * client, or about nothing at all.
 */
export function threadScope(
  placement: DockScope,
  activeTask: ActiveTaskThread | null
): DockScope {
  if (!activeTask) return placement;

  // TASK-786: the KYC workspace keeps its own scope even for the task's OWN
  // client. `dockChatStores` exempts only `scopeType === 'kyc'` from persistence
  // (TASK-719), and the KYC extraction side channel is gated on the placement —
  // so a task thread here would commit KYC transcript and proposal content into
  // a persisting store and write it to sessionStorage, which is precisely what
  // that boundary exists to prevent. The boundary belongs to the SURFACE, so the
  // surface wins. See `dockChatStores.persistMessages` for the other half.
  if (placement.scopeType === 'kyc') return placement;

  // TASK-787: any placement naming an entity that is not the task's own client
  // wins. A meeting route resolves to `{type:'meeting'}` and so named no client
  // the old comparison could see — which let client A's task thread render on
  // client B's meeting page, and let B's brief/proposal chunks commit into A's
  // persisted thread. Matching positively on "the task's own client" closes that
  // for every entity type, including any added later.
  const routeEntity = placement.entity;
  if (routeEntity && !(routeEntity.type === 'client' && routeEntity.id === activeTask.clientId)) {
    return placement;
  }

  return {
    scopeType: 'task',
    entityId: activeTask.taskId,
    label: activeTask.title,
    // The entity envelope stays the task's client, so the agent's context is
    // the client the work is FOR — which is the route's client or no client.
    entity: activeTask.clientId ? { type: 'client', id: activeTask.clientId } : undefined,
  };
}

/**
 * Route → dock PLACEMENT scope (which surface, which entity envelope). Client-
 * name enrichment happens in the Dock (react-query). Since TASK-769 this is one
 * of two independent inputs: `threadScope` decides which conversation goes in it.
 */
export function resolveDockScope(pathname: string): DockScope {
  const { entity, portfolioId } = entityForPath(pathname);
  // TASK-716: the KYC workspace is its own scope — `kyc:<clientA>` and
  // `kyc:<clientB>` are different threads (scope-identity decision), and the
  // kyc scope carries the extraction-proposal side channel.
  if (entity?.type === 'client' && pathname.includes('/kyc-workspace')) {
    return { scopeType: 'kyc', entityId: entity.id, label: 'KYC', entity };
  }
  if (entity?.type === 'client') {
    return { scopeType: 'client', entityId: entity.id, label: 'Client', entity, portfolioId };
  }
  if (entity?.type === 'meeting') {
    return { scopeType: 'meeting', entityId: entity.id, label: 'Meeting', entity };
  }
  const [head] = pathname.split('/').filter(Boolean);
  const labels: Record<string, string> = {
    meetings: 'Meetings',
    tasks: 'Tasks',
    alerts: 'Alerts',
    portfolio: 'Portfolio',
    compliance: 'Compliance',
    documents: 'Documents',
    settings: 'Settings',
  };
  return { scopeType: 'generic', entityId: head ?? null, label: labels[head] ?? 'Assistant' };
}

/**
 * TASK-713 temporary suppression list: routes whose embedded copilot has not
 * migrated yet — mounting the bottom dock beside it would put two live chat
 * inputs on one screen. TASK-715/716/717 each delete their route; end state
 * is an empty list (placements become the only mechanism).
 *
 * `/dashboard` is NOT temporary: the dashboard placement of the dock IS the
 * canvas InputDock (TASK-712) — the bottom-bar-with-sheet never mounts there.
 */
export const DOCK_SUPPRESSED_ROUTES: ReadonlyArray<string> = [
  // TASK-717 PLACEMENT RULE (permanent, like /dashboard): on workbench routes
  // the RAIL is the dock — WorkbenchRail (components/dock/) is the one input,
  // `/` focuses it, and the bottom bar never doubles it. Side-by-side edit
  // review with hover-preview is a genuine side-by-side task (plan §2.2).
  '/workbench',
  // TASK-715 removed '/prepare', TASK-716 removed '/kyc-workspace' — the dock
  // IS the copilot on those routes; their panels are deleted. The temporary
  // migration list is now EMPTY: every entry above is a placement rule.
];

export function isDockSuppressed(pathname: string): boolean {
  if (pathname.startsWith('/dashboard')) return true; // InputDock is the dock here
  return DOCK_SUPPRESSED_ROUTES.some((fragment) => pathname.includes(fragment));
}
